import type { CheckoutSession, StoredCard } from "@/types";
import { PaymentMethod } from "@/types";
import {
  encodeCurrency,
  encodeCardScheme,
  encodeStoredCardStatus,
} from "@/lib/codec";

import { getTracker, trackSessionEvent } from "./analytics-helpers";

export function emitPaymentMethodsShown(
  session: CheckoutSession,
  savedCards:
    | { cards: StoredCard[]; metaData?: { applePayPreference?: boolean } }
    | null
    | undefined,
  applePayAvailable: boolean,
  deviceSupportsApplePay: boolean,
): void {
  const tracker = getTracker();
  if (
    !tracker ||
    !session.merchant?.id ||
    !session.id ||
    !session.order?.id ||
    !session.customerId ||
    !session.order?.countryCode
  ) {
    return;
  }

  const currencyCode = encodeCurrency(session.order.currency) ?? "SAR";

  const paymentMethods: Array<{
    type: string;
    id?: number;
    payment_method?: string;
    scheme?: string;
    last4?: string;
    status?: string;
    is_debit?: boolean;
    is_prepaid?: boolean;
    expiry_year?: number;
    expiry_month?: number;
    scheme_local?: string;
    is_auto_charge?: boolean;
    is_disabled_cvv?: boolean;
    card_id?: number;
  }> = [];

  if (applePayAvailable) {
    paymentMethods.push({ type: "apple_pay" });
  }

  if (savedCards?.cards && savedCards.cards.length > 0) {
    savedCards.cards.forEach((card) => {
      const encodedScheme = card.scheme ? encodeCardScheme(card.scheme) : null;
      const encodedSchemeLocal = card.schemeLocal
        ? encodeCardScheme(card.schemeLocal)
        : null;
      const encodedStatus = encodeStoredCardStatus(card.status);
      paymentMethods.push({
        type: "stored_card",
        id: card.id,
        card_id: card.id,
        payment_method: PaymentMethod[card.paymentMethod] ?? "UNKNOWN",
        scheme: encodedScheme ?? undefined,
        last4: card.last4,
        status: encodedStatus ?? undefined,
        is_debit: card.isDebit,
        is_prepaid: card.isPrepaid,
        expiry_year: card.expiryYear,
        expiry_month: card.expiryMonth,
        scheme_local: encodedSchemeLocal ?? undefined,
        is_auto_charge: card.isAutoCharge,
        is_disabled_cvv: card.isDisabledCvv,
      });
    });
  }

  tracker.track({
    eventType: "payment_methods_shown",
    merchantId: session.merchant.id,
    checkoutId: session.id,
    orderId: session.order.id,
    customerId: session.customerId,
    countryCode: session.order.countryCode,
    currencyCode,
    eventData: {
      payment_methods: paymentMethods,
      apple_pay_available: applePayAvailable,
      device_supports_apple_pay: deviceSupportsApplePay,
      total_payment_methods_count: paymentMethods.length,
      saved_cards_count: savedCards?.cards?.length ?? 0,
      apple_pay_preference: savedCards?.metaData?.applePayPreference ?? null,
    },
  });
}

export function emitPaymentSubmissionTracking(
  eventType:
    | "payment_submission_started"
    | "payment_submission_completed"
    | "payment_submission_failed"
    | "cvv_submission_started"
    | "cvv_submission_completed"
    | "cvv_submission_failed",
  session: CheckoutSession,
  eventData: {
    payment_method_type?: "card" | "apple_pay" | "wallet_only" | "pnm_confirm";
    card_id?: string;
    wallet_selected?: boolean;
    amount?: number;
    document_signature_pending?: boolean;
    error_message?: string;
  },
): void {
  trackSessionEvent(eventType, session, eventData);
}

export function emitPaymentMethodSelectionTracking(
  eventType:
    | "payment_method_selected"
    | "payment_method_deselected"
    | "apple_pay_selected",
  session: CheckoutSession,
  eventData: {
    payment_method_type?: "card" | "apple_pay" | "wallet";
    card_id?: number;
    card_scheme?: string;
    card_last4?: string;
    card_is_debit?: boolean;
    card_is_prepaid?: boolean;
    card_is_auto_charge?: boolean;
    wallet_balance?: number;
  },
): void {
  trackSessionEvent(eventType, session, eventData);
}

export function emitApplePayTracking(
  eventType:
    | "apple_pay_initiated"
    | "apple_pay_canceled"
    | "apple_pay_failed"
    | "apple_pay_completed"
    | "apple_pay_available"
    | "apple_pay_button_shown"
    | "apple_pay_session_started"
    | "apple_pay_session_validated"
    | "apple_pay_payment_completed"
    | "apple_pay_payment_failed"
    | "apple_pay_payment_canceled",
  session: CheckoutSession,
  eventData?: Record<string, unknown>,
): void {
  trackSessionEvent(eventType, session, eventData ?? {});
}

export function emitCardFormTracking(
  eventType:
    | "new_card_form_opened"
    | "new_card_form_closed"
    | "new_card_form_submitted"
    | "card_tokenization_started"
    | "card_tokenization_completed"
    | "card_tokenization_failed"
    | "card_form_validation_error",
  session: CheckoutSession,
  eventData?: {
    card_scheme?: string;
    validation_errors?: string[];
    tokenization_error?: string;
  },
): void {
  trackSessionEvent(eventType, session, eventData ?? {});
}

export function emitSlowPaymentLoadingTracking(
  session: CheckoutSession,
  elapsedTimeMs: number,
): void {
  trackSessionEvent("slow_payment_loading", session, {
    elapsed_time_ms: elapsedTimeMs,
    elapsed_time_seconds: Math.floor(elapsedTimeMs / 1000),
  });
}
